import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RegistrationSuccess = ({ 
  email, 
  onResendVerification, 
  onSkipVerification,
  resendLoading = false 
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleResend = async () => {
    await onResendVerification();
    setCountdown(60);
    setCanResend(false);
  };

  const handleSkip = () => {
    if (onSkipVerification) {
      onSkipVerification();
    } else {
      navigate('/user-account-dashboard');
    }
  };

  return (
    <div className="text-center space-y-6">
      {/* Success Icon */}
      <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
        <Icon name="CheckCircle" size={32} className="text-success" />
      </div>

      {/* Success Message */}
      <div className="space-y-2">
        <h2 className="font-heading text-2xl font-bold text-foreground">
          Account Created Successfully!
        </h2>
        <p className="font-body text-muted-foreground">
          We've sent a verification email to
        </p>
        <p className="font-body font-medium text-foreground">
          {email}
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-muted rounded-lg p-4 space-y-3">
        <div className="flex items-start space-x-3">
          <Icon name="Mail" size={20} className="text-primary mt-0.5" />
          <div className="text-left">
            <h3 className="font-body font-medium text-foreground mb-1">
              Check your email
            </h3>
            <p className="font-caption text-sm text-muted-foreground">
              Click the verification link in your email to activate your account and access all features.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Icon name="Clock" size={20} className="text-warning mt-0.5" />
          <div className="text-left">
            <h3 className="font-body font-medium text-foreground mb-1">
              Didn't receive it?
            </h3>
            <p className="font-caption text-sm text-muted-foreground">
              Check your spam folder or request a new verification email below.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          variant="outline"
          onClick={handleResend}
          disabled={!canResend || resendLoading}
          loading={resendLoading}
          iconName="RefreshCw"
          iconPosition="left"
          fullWidth
        >
          {canResend 
            ? 'Resend Verification Email' 
            : `Resend in ${countdown}s`
          }
        </Button>

        <Button
          variant="default"
          onClick={handleSkip}
          iconName="ArrowRight"
          iconPosition="right"
          fullWidth
        >
          Continue to Dashboard
        </Button>
      </div>

      {/* Additional Help */}
      <div className="pt-4 border-t border-border">
        <p className="font-caption text-xs text-muted-foreground mb-3">
          Having trouble? Contact our support team
        </p>
        <div className="flex items-center justify-center space-x-4">
          <button className="flex items-center space-x-1 text-primary hover:text-primary/80 transition-colors">
            <Icon name="MessageCircle" size={14} />
            <span className="font-caption text-xs">Live Chat</span>
          </button>
          <button className="flex items-center space-x-1 text-primary hover:text-primary/80 transition-colors">
            <Icon name="Mail" size={14} />
            <span className="font-caption text-xs">Email Support</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;